import { dateAddWeeks, dateFormatYMD } from './dateFns.js';
import { l, lLink, lc, lm, lreplace, overrideTranslationFunctions } from '@js/plugins/translations.js';
import { Base64 } from '../plugins/base64.js';
import { ConnectApi } from '@js/plugins/connectApi';
import { isLocalStorageConsentGiven } from '@js/utils/cookieConsent';

export const EventDispatcher = createDispatcher();
export const linkPrefix = {
	assetPrefix: '/static/v' + (window?.js_params?.version || '1.0.0'),
	base: '',
	connectApiBase: ''
};

function createDispatcher () {
	const listeners = new Map();

	const api = {
		/**
		 * Subscribe to an event.
		 * @param {string} event
		 * @param {Function} callback
		 * @returns {Function} the callback (for easy removal)
		 */
		addEventListener (event, callback) {
			if (!listeners.has(event)) {
				listeners.set(event, new Set());
			}
			listeners.get(event).add(callback);
			return callback;
		},

		/**
		 * Unsubscribe a previously-added listener.
		 * @param {string} event
		 * @param {Function} callback
		 * @returns {boolean} whether a listener was removed
		 */
		removeEventListener (event, callback) {
			const set = listeners.get(event);
			if (!set) {
				return false;
			}
			const removed = set.delete(callback);
			if (set.size === 0) {
				listeners.delete(event);
			}
			return removed;
		},

		/**
		 * Subscribe to an event just once.
		 * @param {string} event
		 * @param {Function} callback
		 * @returns {Function} the wrapped callback
		 */
		once (event, callback) {
			const wrapped = (...args) => {
				callback(...args);
				api.removeEventListener(event, wrapped);
			};
			api.addEventListener(event, wrapped);
			return wrapped;
		},

		/**
		 * Emit an event with any number of args.
		 * @param {string} event
		 * @param  {...any} args
		 */
		dispatch (event, ...args) {
			const set = listeners.get(event);
			if (!set) {
				return;
			}

			// Directly iterate over the Set
			for (const fn of set) {
				fn(...args);
			}
		}
	};

	return api;
}


/**
 * Sets the dbg cookie
 * @param {Boolean} shouldOpen
 */
export function debugMenu (shouldOpen = 1) {
	shouldOpen = 1 * shouldOpen;
	const cookieName = 'dbg';
	if (!shouldOpen) {
		eraseCookie(cookieName);
		storedItemCreate('debug_menu_state', shouldOpen);
		return;
	}
	storedItemCreate('debug_menu_state', shouldOpen);
	setCookie(cookieName, shouldOpen);
	location.reload();
}

/**
 * Sets the force_server cookie
 * @param {Number} serverId
 */
export function forceServer (serverId) {
	const cookieName = 'force_server';
	if (serverId === null) {
		eraseCookie(cookieName);
		return;
	}
	setCookie(cookieName, serverId);
}

/**
 * Get the index of an element
 * @param {Element} child
 * @returns {number}
 */
export const getNodeIndex = child => [...child.parentNode.children].findIndex(c => c == child);

/**
 * Returns the height of the document
 * @returns {number}
 */
export function getDocumentHeight () {
	const body = document.body;
	const html = document.documentElement;
	return Math.max(
		body.offsetHeight,
		body.scrollHeight,
		html.clientHeight,
		html.offsetHeight,
		html.scrollHeight
	);
}

/**
 * Get the properly formatted tracking class name
 * @param {string} className the core class name
 * @returns the formatted class name
 */
export function getTrackingClass (className) {
	return `trck_${className}`;
}

/**
 * Get all children element preceding the given child element
 * @param {Element} element DOM element - the child element
 * @returns the list of all preceding siblings
 */
export function prevAll (element) {
	const result = [];

	while ((element = element.previousElementSibling)) {
		result.push(element);
	}

	return result;
}


/**
 * Check if currently on the homepage
 * @returns {boolean} True if body contains the class "home"
 */
export function isHomePage () {
	return document.body.classList.contains('home');
}

/**
 * Check if currently on the showresult page
 * @returns {boolean} True if body contains the class "showresult"
 */
export function isShowresultPage () {
	return !!globalThis.js_params.isShowresult;
}

/**
 * Used to check if mobile
 * @returns {boolean} true if window innerWidth is less than 768
 */
export function isMobileView () {
	return globalThis.innerWidth < 768;
}

export function isTabletView () {
	return globalThis.innerWidth >= 768 && globalThis.innerWidth < 992;
}

/**
 * Used to check if desktop
 * @returns {boolean} true if window innerWidth is greater that or equal to 992
 */
export function isDesktopView () {
	return globalThis.innerWidth >= 992;
}

/**
 * Check if currently fullscreen
 * @returns {boolean} True if body contains the class "no-scroll"
 */
export function isFullScreen () {
	return document.body.classList.contains('no-scroll');
}

/**
 * Add 7 days
 * @param {string} strDate
 * @returns {string} Formatted "yyyy-mm-dd"
 */
export function add7Days (strDate) {
	return dateFormatYMD(dateAddWeeks(new Date(strDate), 1));
}

/**
 * Add 14 days
 * @param {string} strDate
 * @returns {string} Formatted "yyyy-mm-dd"
 */
export function add14Days (strDate) {
	return dateFormatYMD(dateAddWeeks(new Date(strDate), 2));
}

/**
 * Transform an array of object into an array of strings
 * @param {array} arr
 * @returns {array}
 */
export function multiDimensionalUnique (arr) {
	const uniques = [];
	const itemsFound = {};
	const arrLength = arr.length;

	for (let i = 0; i < arrLength; i++) {
		const stringified = JSON.stringify(arr[i]);

		if (itemsFound[stringified]) {
			continue;
		}

		uniques.push(arr[i]);

		itemsFound[stringified] = true;
	}

	return uniques;
}

/**
 * Shuffle an array
 * @param {array} array Array to shuffle
 * @returns {array} New array shuffled
 */
export function shuffleArray (arr) {
	return arr
		.map(a => [Math.random(), a])
		.sort((a, b) => a[0] - b[0])
		.map(a => a[1]);
}

/**
 * Check if Safari
 * @returns {boolean} True if vendor or user agent matches the correct parameters
 */
export function isSafari () {
	return (
		navigator.vendor
		&& navigator.vendor.includes('Apple')
		&& navigator.userAgent
		&& !navigator.userAgent.includes('CriOS')
		&& !navigator.userAgent.includes('FxiOS')
	);
}

/**
 * Check if email is valid using regex
 * @param {string} email Email address to validate
 * @returns {boolean}
 */
export function isValidEmail (email) {
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * Prepend a zero for numbers lower then 10
 * @param {string|number} num
 * @returns {string}
 */
export function addZero (num) {
	return ('0' + num).slice(-2);
}

/**
 * Get a cookie value
 * @param {string} name Cookie name to get
 * @returns {string|null}
 */
export function getCookie (name) {
	const nameEQ = name + '=';
	const ca = document.cookie.split(';');

	for (let i = 0; i < ca.length; i++) {
		let c = ca[i];

		while (c.charAt(0) == ' ') {
			c = c.substring(1, c.length);
		}

		if (c.indexOf(nameEQ) === 0) {
			return c.substring(nameEQ.length, c.length);
		}
	}

	return null;
}

/**
 * Set a cookie value
 * @param {string} name Name of cookie value to set
 * @param {string|number} value Value to set
 * @param {string|number} days Number of days the cookie should be valid
 * @param {string|number} seconds Number of days the cookie should be valid
 */
export function setCookie (name, value, days, seconds) {
	let expires = '';

	if (days) {
		const date = new Date();

		seconds = seconds ? seconds * 1000 : 0;
		date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000 + seconds));

		expires = '; expires=' + date.toGMTString();
	}

	document.cookie = name + '=' + value + expires + '; path=/';
}

/**
 * Remove a cookie
 * @param {string} name Cookie to remove
 */
export function eraseCookie (name) {
	setCookie(name, '', -1);
}

/**
 * Get URL parameters
 * @returns {string}
 */
export function getUrlVars (url = globalThis.location.href) {
	// const urlParams = new URLSearchParams(location.search);
	// const params = Object.fromEntries(urlParams);
	// return params
	const vars = {};
	url.replace(/[?&]+([^=&]+)=([^&|#]*)/gi, function (
		m,
		key,
		value
	) {
		vars[key] = value;
	});

	return vars;
}

/**
 * Update current URL with new parameters
 * @param {string} key
 * @param {string} value
 * @param {boolean} pushState - If true, use a new history entry will be created
 */
export function urlParameterSet (key, value, pushState = false) {
	try {
		const url = new URL(globalThis.location.href);
		url.searchParams.set(key, value);
		if (pushState) {
			globalThis.history.pushState({}, '', url);
		} else {
			globalThis.history.replaceState({}, '', url);
		}
		// update url without updating the history
	} catch (error) {
		console.warn('Error updating URL parameter', error);
	}
}

/**
 *
 * @param {string} key
 * @param {boolean} pushState - If true, use a new history entry will be created
 */
export function urlParameterDelete (key, pushState) {
	try {
		const url = new URL(globalThis.location.href);
		url.searchParams.delete(key);
		if (pushState) {
			globalThis.history.pushState({}, '', url);
		} else {
			globalThis.history.replaceState({}, '', url);
		}
	} catch (error) {
		console.warn('Error deleting URL parameter', error);
	}
}

export function urlParameterGet (key) {
	const url = new URL(globalThis.location.href);
	return url.searchParams.get(key);
}

/**
 * Set a URL parameter
 * @param {string|number} url full URL string
 * @param {string|number} parameterName parameter name
 * @param {string|number} parameterValue parameter value to set
 * @param {boolean} atStart if the property should be added to the start of the url
 * @returns {string} full URL with the new parameter
 */
export function setUrlParameter (url, name, value) {
	const [pathAndQuery, hash = ''] = url.split('#');
	const [path, query = ''] = pathAndQuery.split('?');

	const params = new URLSearchParams(query);

	if (value == null) {
		params.delete(name);
	} else {
		params.set(name, value);
	}

	const qs = params.toString();
	return path + (qs ? `?${qs}` : '') + (hash ? `#${hash}` : '');
}


export function range (start, end, step) {
	const range_ = [];
	const typeofStart = typeof start;
	const typeofEnd = typeof end;

	if (step === 0) {
		throw new TypeError('Step cannot be zero.');
	}

	if (typeofStart == 'undefined' || typeofEnd == 'undefined') {
		throw new TypeError('Must pass start and end arguments.');
	} else if (typeofStart != typeofEnd) {
		throw new TypeError('Start and end arguments must be of same type.');
	}

	typeof step === 'undefined' && (step = 1);

	if (end < start) {
		step = -step;
	}

	if (typeofStart == 'number') {
		while (step > 0 ? end >= start : end <= start) {
			range_.push(start);
			start += step;
		}
	} else if (typeofStart == 'string') {
		if (start.length != 1 || end.length != 1) {
			throw new TypeError('Only strings with one character are supported.');
		}

		start = start.charCodeAt(0);
		end = end.charCodeAt(0);

		while (step > 0 ? end >= start : end <= start) {
			range_.push(String.fromCharCode(start));
			start += step;
		}
	} else {
		throw new TypeError('Only string and number types are supported');
	}

	return range_;
}


/**
 * Create a new array
 * @param {array} array an array of objects
 * @param {string} key key with unique properties
 * @returns {array}
 */
export function arrayUniqueByKey (array, key) {
	return [...new Map(array.map(item => [item[key], item])).values()];
}


/**
 * Converts seconds into days, hours, minutes
 * @param {number|string} seconds The number of seconds to convert
 * @returns {string}
 */
export function toHHMM (seconds) {
	seconds = Number(seconds);
	const d = Math.floor(seconds / (3600 * 24));
	const h = Math.floor(seconds % (3600 * 24) / 3600);
	const m = Math.floor(seconds % 3600 / 60);
	// const s = Math.floor(seconds % 60)

	// const dDisplay = d > 0 ? d + (d === 1 ? ' day, ' : ' days, ') : '';
	// const hDisplay = h > 0 ? h + (h === 1 ? ' hour, ' : ' hours, ') : '';
	// const mDisplay = m > 0 ? m + (m === 1 ? ' minute' : ' minutes') : '';
	const dDisplay = d > 0 ? d + (d === 1 ? 'd,' : 'd, ') : '';
	const hDisplay = h > 0 ? h + (h === 1 ? 'h,' : 'h, ') : '';
	const mDisplay = m > 0 ? m + (m === 1 ? 'm' : 'm') : '';
	// const sDisplay = s > 0 ? s + (s === 1 ? ' second' : ' seconds') : ''
	// return dDisplay + hDisplay + mDisplay + sDisplay
	return dDisplay + hDisplay + mDisplay;
}

/**
 * Converts seconds into hours and minutes
 * @param {Number} traveltime - the traveltime in seconds
 * @returns {String} the traveltime in hours and minutes
 */
export function getTimeText (traveltime) {
	if (
		typeof traveltime === 'number'
		&& traveltime != 0
		&& traveltime != Number.MAX_VALUE
	) {
		const time = String(traveltime / 60 / 60);
		const timeArr = time.split('.');
		let minutes = '';

		// in german formatting, add space before hours and minutes
		const germanSpacing = globalThis.js_params.language == 'de' ? ' ' : '';

		if (timeArr.length == 2) {
			minutes
			= ' '
			+ Math.round(Number('0.' + String(timeArr[1])) * 60)
			+ germanSpacing
			+ l('time.minutesShort');
		}

		const hours = timeArr[0];

		return hours + germanSpacing + l('time.hoursShort') + minutes;
	}

	return l('global.simplePhrases.missing');
}


/**
 *
 * @param {object} args Expected an object containing two strings: code and size
 * @returns {string} URL to agency image
 */
export function getAgencyImagePath (args) {
	let { code, size } = args;
	const { domainSpecificAgencyLogos, version } = globalThis.js_params;

	const codeSplit = code.split('_');
	let domain = 'default';

	size = size || 'regular';

	if (domainSpecificAgencyLogos) {
		if (domainSpecificAgencyLogos.includes(codeSplit[1])) {
			domain = codeSplit[0];
		}
	}

	return `${linkPrefix.assetPrefix}/images/logos_agency/${size}/${domain}/${codeSplit[1]}.png`;
}

/** Get the formatted config for the Searchbox from URL variables
	 @param  urlVars   object - the dictionary of available URL variables

	 @return the searchbox config object in the proper format to instantiate the searchbox fields **/
export function getSearchBoxConfigFromUrl (urlVars) {
	const searchBoxConfig = {};

	if ('initSearch' in globalThis.js_params && globalThis.js_params.initSearch.length != 0) {
		searchBoxConfig.legs = [];
		const initSearch = globalThis.js_params.initSearch;
		const legs = {};

		if (initSearch?.from && initSearch?.fromIata) {
			legs.from = initSearch.from;
			legs.fromIata = initSearch.fromIata;
			legs.fromMetro = initSearch.fromMetro;
			legs.fromIatas = [
				{
					iata: initSearch.fromIata,
					text: initSearch.from,
					isMetro: initSearch.fromMetro
				}
			];
		}

		if (initSearch?.to && initSearch?.toIata) {
			legs.to = initSearch.to;
			legs.toIata = initSearch.toIata;
			legs.toMetro = initSearch.toMetro;
			legs.toIatas = [
				{
					iata: initSearch.toIata,
					text: initSearch.to,
					isMetro: initSearch.toMetro
				}
			];
		}

		if (urlVars.dateFrom) {
			legs.departDate = urlVars.dateFrom;
		}

		if (urlVars.dateTo) {
			legs.returnDate = urlVars.dateTo;
		}

		searchBoxConfig.legs.push(legs);
	}

	searchBoxConfig.passengers = {
		adults: 1,
		child: {
			count: 0,
			ages: []
		},
		youth: {
			count: 1,
			ages: [null]
		},
		activeView: 'default',
		isYouthTicketEnabled: true
	};

	if ('adults' in urlVars) {
		searchBoxConfig.passengers.adults = parseInt(urlVars.adults);
	}

	if ('children' in urlVars) {
		const children = parseInt(urlVars.children);
		searchBoxConfig.passengers.child.count = children;
		searchBoxConfig.passengers.child.ages = urlVars.childrenAges
			? urlVars.childrenAges.split(',').map(str => parseInt(str))
			: Array.from({ length: children }, () => null);
	}


	if ('youth' in urlVars) {
		searchBoxConfig.passengers.youth.count = parseInt(urlVars.youth);
		searchBoxConfig.passengers.isYouthTicketEnabled = searchBoxConfig.passengers.youth.count > 0;
		searchBoxConfig.passengers.activeView = searchBoxConfig.passengers.isYouthTicketEnabled ? 'youth' : 'default';
		searchBoxConfig.passengers.youth.ages = ('youthAges' in urlVars)
			? urlVars.youthAges.split(',').map(str => parseInt(str))
			: Array.from({ length: searchBoxConfig.passengers.youth.count }, () => null);
	}

	if (!('youth' in urlVars) && !('children' in urlVars) && !('adults' in urlVars)) {
		delete searchBoxConfig.passengers;
	}

	return searchBoxConfig;
}

/** Deep copy an object
	 @param  object   object - the object to deep copy

	 @return a new copy of the object, returns false if object is not a string or an object
	**/
export function deepCopy (object) {
	if (typeof object === 'object') {
		return JSON.parse(JSON.stringify(object));
	}
	// if (typeof object === 'string') {
	// 	return JSON.parse(object);
	// }
	return false;
}

export const storageAvailableMap = {
	localStorage: storageAvailable('localStorage'),
	sessionStorage: storageAvailable('sessionStorage')
};

export const isLocalStorageAvailable = storageAvailableMap.localStorage;
export const isSessionStorageAvailable = storageAvailableMap.sessionStorage;


/** Stringify then set item to localStorage
	 @param  keyname   string - reference
	 @param  value   value that needs being stored in localStorage
	 @param  storageType - localStorage or sessionStorage
	 @param  type - marketing or functional

	 @return A String, representing the inserted value
	**/
export function storedItemCreate (keyname, value, storageType = 'localStorage', type = 'marketing') {
	if (storageType === 'localStorage' && type === 'marketing' && !isLocalStorageConsentGiven()) {
		console.warn('Cookie consent not given - not storing item in localStorage');
		return;
	}

	if (!storageAvailableMap[storageType]) {
		console.warn(`${storageType} is not available`);
		return null;
	}
	if (typeof keyname !== 'string') {
		throw new TypeError('Keyname can only be of type string.');
	}
	if (value === undefined) {
		console.warn('Trying to save an undefined value to localStorage - value not stored');
		return null;
	}
	try {
		window[storageType].setItem(keyname, JSON.stringify(value));
	} catch (e) {
		console.log(`Could not save item to ${storageType}. Please, make sure ${storageType} is enabled for a better user experience on our website. `, e);
	}
}

/** Retrieve then parse localStorage item
	 @param  keyname   string - reference
	 @param  storageType - localStorage or sessionStorage
	 @return A String, representing the value of the specified key when it exists or NULL
	 **/
export function storedItemRead (keyname, storageType = 'localStorage') {
	if (!storageAvailableMap[storageType]) {
		console.warn(`${storageType} is not available`);
		return null;
	}
	// Check proper use of the function
	if (typeof keyname !== 'string') {
		throw new TypeError('Keyname can only be of type string.');
	}
	// Fetch item
	let item = window[storageType].getItem(keyname);
	if (!item) {
		return null;
	}
	try {
		item = JSON.parse(item);
	} catch (e) {
		console.warn('Compromised value stored in localStorage - removing item');
		storedItemDelete(keyname, storageType);
		return null;
	}
	return item;
}


/** Remove an item from local storage
	 @param  keyname   string - reference
	 **/
export function storedItemDelete (keyname, storageType = 'localStorage') {
	if (!storageAvailableMap[storageType]) {
		console.warn(`${storageType} is not available`);
		return null;
	}
	if (typeof keyname !== 'string') {
		throw new TypeError('Keyname can only be of type string.');
	}
	try {
		window[storageType].removeItem(keyname);
	} catch (e) {
		console.log(`Could not remove saved item from ${storageType}. Please, make sure ${storageType} is enabled for a better user experience on our website. `, e);
	}
}

// https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API#feature-detecting_localstorage
/**
 *
 * @param {String} type - localStorage or sessionStorage
 * @returns {Boolean} - true if available
 */
function storageAvailable (type) {
	let storage;
	try {
		storage = window[type];
		const x = '__storage_test__';
		storage.setItem(x, x);
		storage.removeItem(x);
		return true;
	} catch (e) {
		const isStorageAvailable = (
			// everything except Firefox
			e?.code === 22
			// Firefox
			|| e?.code === 1014
			// test name field too, because code might not be present
			// everything except Firefox
			|| e?.name === 'QuotaExceededError'
			// Firefox
			|| e?.name === 'NS_ERROR_DOM_QUOTA_REACHED')
			// acknowledge QuotaExceededError only if there's something already stored
			&& (storage && storage.length !== 0);
		if (!isStorageAvailable) {
			// Civet.reportError(new Error(`${type} is not available`));
		}
		return isStorageAvailable;
	}
}


/**
	 * Alias for document.querySelector
 * @param {string} selector query selector string
 * @param {Element} [scope] element to query against, defaults to document
 * @returns {Element}
 */
export function qs (selector, scope = window.document) {
	return scope.querySelector(selector);
}

/**
 * Alias for document.querySelectorAll
 * @param {string} selector query selector string
 * @param {Element} [scope] element to query against, defaults to document
 * @returns {array} array of Elements
 */
export function qsa (selector, scope = window.document) {
	return [...scope.querySelectorAll(selector)];
}

/**
 * Alias for document.getElementById
 * @param {string} selector query selector string
 * @param {Element} [scope] element to query against, defaults to document
 * @returns {Element}
 */
export function gebi (selector, scope = window.document) {
	return scope.getElementById(selector);
}

/**
 * Alias for getElementsByClassName
 * @param {string} s query selector string
 * @param {Element} [o] element to query against, defaults to document
 * @returns {Element}
 */
export function gebcn (s, o = document) {
	return o.getElementsByClassName(s);
}

/**
 * Sleep for a given amount of time
 * @param {number} ms number of milliseconds to sleep
 * @returns {Promise} resolves when the number of milliseconds requested has passed
 */
export function sleep (ms) {
	return new Promise(resolve => setTimeout(resolve, ms));
}


/**
 * @param {Element|String|Array} element Element to be shown or hidden
 * @param show Controls if the element should be shown or hidden
 * @returns {Element}
 */
export function showElement (element, show = true) {
	if (Array.isArray(element)) {
		return element.forEach(el => showElement(el, show));
	}
	// hideElement will call checkForElement
	if (!show) {
		return hideElement(element);
	}
	const elementReal = checkForElement(element);
	if (!elementReal) {
		console.warn('showElement: Didn\'t find element', element);
		return;
	}
	// Check the inline property
	if (elementReal.style.display === 'none') {
		elementReal.style.removeProperty('display');
	}
	// If the element is still hidden after removing the inline property
	if (isHidden(elementReal)) {
		elementReal.style.display = 'block';
	}
	return element;
}

/**
 * @param {Element|String|Array} element Element to be hidden
 * @returns {Element}
 */
export function hideElement (element, hide = true) {
	if (Array.isArray(element)) {
		return element.forEach(el => hideElement(el, hide));
	}
	if (!hide) {
		return showElement(element, false);
	}
	const elementReal = checkForElement(element);
	if (!elementReal) {
		console.warn('hideElement: Didn\'t find element', element);
		return {};
	}
	if (isHidden(elementReal)) {
		console.warn('hideElement: Element already hidden', element);
		return elementReal;
	}
	elementReal.style.display = 'none';
	return elementReal;
}

/**
 *
 * @param {Element|String|Array} element
 * @returns {boolean} true if element is visible
 */
export function toggleVisible (element) {
	if (Array.isArray(element)) {
		return element.forEach(el => toggleVisible(el));
	}

	const elementReal = checkForElement(element);

	if (!elementReal) {
		console.warn('toggleVisible: Didn\'t find element', element);
		return;
	}
	const _isHidden = isHidden(elementReal);
	if (_isHidden) {
		showElement(elementReal);
	} else {
		hideElement(elementReal);
	}
	return !_isHidden;
}

/**
 * Used internally by showElement, hideElement, slideToggle, must be given a real element and not a query selector
 * @param {Element} element
 * @returns {Boolean} true if the computed display of the element is 'none'
 */
export function isHidden (element) {
	return getComputedStyle(element).display === 'none';
}

function isElement (element) {
	return (element instanceof Element);
}

function checkForElement (element) {
	return isElement(element) ? element : qs(element);
}

/** Filter function for non iterable object based on key values
	 @param  obj   		object - object to filter within
	 @param  callback   function - filtering function to apply on key values
	 **/
export function filterObjectByKeys (obj, callback) {
	if (!obj) {
		throw new TypeError('obj must not be null');
	}
	if (typeof obj !== 'object') {
		throw new TypeError('obj must be of type array');
	}
	if (typeof callback !== 'function') {
		throw new TypeError('callback must be a function');
	}

	const objAsArray = Object.entries(obj);
	const filtered = objAsArray.filter(([key, value]) => callback(key));

	// No full support for Object.fromEntries at the moment (IE, Opera mini)
	const filteredAsObject = {};
	filtered.forEach((entry) => {
		filteredAsObject[entry[0]] = entry[1];
	});

	return filteredAsObject;
}

/**
 * Slides an element up using the Web Animations API
 * @param {Element} element Element to slide up
 * @param {number} durationInMs Number of milliseconds the animation should run for
 * @param {string} [easing = 'ease-out'] linear|ease|ease-in|ease-out|ease-in-out|step-start|step-end|steps(int,start|end)|cubic-bezier(n,n,n,n)|initial|inherit
 */
export function slideUp (element, durationInMs = 400, easing = 'ease-out') {
	element = checkForElement(element);
	if (!element) {
		console.log('slideUp: couldn\'t find element', element);
		return Promise.resolve(element);
	}
	if (element.dataset.slidUp) {
		console.log('slideUp: Element already slid up');
		return;
	}
	element.removeAttribute('data-slid-down');
	element.dataset.slidUp = true;

	const keyframes = (element) => {
		element.style.overflow = 'hidden';
		const height = element.offsetHeight + 'px';
		const { marginTop, marginBottom, paddingTop, paddingBottom } = getComputedStyle(element);
		return [
			{
				height,
				marginTop,
				marginBottom,
				paddingTop,
				paddingBottom
			},
			{
				height: 0,
				marginTop: 0,
				marginBottom: 0,
				paddingTop: 0,
				paddingBottom: 0
			}
		];
	};

	return animate(element, keyframes, durationInMs, easing).then((element) => {
		hideElement(element);
		element.style.removeProperty('overflow');
		return element;
	}).catch(() => console.log('slideUp: Animation failed'));
}

/**
 * Slides an element down using the Web Animations API
 * @param {Element} element Element to slide down
 * @param {number} durationInMs Number of milliseconds the animation should run for
 * @param {string} [easing='ease-out'] linear|ease|ease-in|ease-out|ease-in-out|step-start|step-end|steps(int,start|end)|cubic-bezier(n,n,n,n)|initial|inherit
 */
export function slideDown (element, durationInMs = 400, easing = 'ease-out') {
	element = checkForElement(element);
	if (!element) {
		console.log('slideDown: couldn\'t find element', element);
		return Promise.resolve(element);
	}
	if (element.dataset.slidDown) {
		console.log('slideDown: Element already slid down');
		return;
	}
	element.removeAttribute('data-slid-up');
	element.dataset.slidDown = true;

	const keyframes = (element) => {
		showElement(element);
		const height = element.offsetHeight + 'px';
		element.style.overflow = 'hidden';
		const { marginTop, marginBottom, paddingTop, paddingBottom } = getComputedStyle(element);

		return [
			{
				height: 0,
				marginTop: 0,
				marginBottom: 0,
				paddingTop: 0,
				paddingBottom: 0
			},
			{
				height,
				marginTop,
				marginBottom,
				paddingTop,
				paddingBottom
			}
		];
	};
	return animate(element, keyframes, durationInMs, easing).then((element) => {
		element.style.removeProperty('overflow');
		return element;
	}).catch(() => console.log('slideUp: Animation failed'));
}

/**
 * Slide and element up or down depending on it's computed display style
 * @param {Element} element element to slide
 * @returns {Promise} resolves when the element has finished animating
 */
export function slideToggle (element) {
	const elementReal = checkForElement(element);
	if (!elementReal) {
		console.log('slideToggle: couldn\'t find element', element);
		return Promise.resolve(elementReal);
	}
	if (isHidden(elementReal)) {
		return slideDown(elementReal);
	} else {
		return slideUp(elementReal);
	}
}

/**
 * Manual reset of already slid up or down element, without animation, to start over on clean slate
 * @param {Element} element element to reset
 */
export function slideReset (element) {
	element = checkForElement(element);
	if (!element) {
		console.log('slideReset: couldn\'t find element', element);
		return;
	}
	element.removeAttribute('data-slid-up');
	element.removeAttribute('data-slid-down');
}

/**
 * Fade an element in using the Web Animations API
 * @param {Element} element Element to fade in
 * @param {number} durationInMs Number of milliseconds the animation should run for
 * @param {string} [easing="ease-out"] linear|ease|ease-in|ease-out|ease-in-out|step-start|step-end|steps(int,start|end)|cubic-bezier(n,n,n,n)|initial|inherit
 * @returns {Promise} Resolves when the element has finished animating
 */
export function fadeIn (element, durationInMs = 400, easing = 'ease-out', isResultsContainer = false) {
	const elementReal = checkForElement(element);
	if (!elementReal) {
		console.log('fadeIn: couldn\'t find element', element);
		return Promise.resolve(element);
	}
	const keyframes = (elementReal) => {
		if (!isHidden(elementReal)) {
			console.log('cancelling fade in, element is already visible', elementReal);
			return [];
		}
		showElement(elementReal);
		return [
			{ opacity: 0 },
			{ opacity: 1 }
		];
	};
	return animate(elementReal, keyframes, durationInMs, easing, isResultsContainer).catch(() => ({}));
}

/**
	* Fade an element out using the Web Animations API
 * @param {Element|String} element Element to fade out
 * @param {number} durationInMs Number of milliseconds the animation should run for
 * @param {string} [easing='ease-out'] linear|ease|ease-in|ease-out|ease-in-out|step-start|step-end|steps(int,start|end)|cubic-bezier(n,n,n,n)|initial|inherit
 * @returns {Promise} Resolves when the element has finished animating
 */
export function fadeOut (element, durationInMs = 400, easing = 'ease-out', isResultsContainer = false) {
	const elementReal = checkForElement(element);
	if (!elementReal) {
		console.log('fadeOut: couldn\'t find element', element);
		return Promise.resolve(elementReal);
	}
	const keyframes = () => {
		return [
			{ opacity: 1 },
			{ opacity: 0 }
		];
	};
	return animate(elementReal, keyframes, durationInMs, easing, isResultsContainer).then(() => {
		if (!isResultsContainer) {
			hideElement(elementReal);
		}
	}).catch(() => ({}));
}

/**
 * Calls element.animate with a series of keyframes, allows the developer to force the element to be shown if it is hidden
 * @param {Element} element element to animate
 * @param {array} keyframesFunction
 * @param {number} durationInMs
 * @param {string} [easing='ease-out'] linear|ease|ease-in|ease-out|ease-in-out|step-start|step-end|steps(int,start|end)|cubic-bezier(n,n,n,n)|initial|inherit
 * @param {boolean} showOnStart if the element is hidden, force it to be shown before the animation
 * @param {boolean} hideOnComplete If the element should be shown when the animation has completed
 * @returns {Promise} resolves when the animation has completed or been cancelled
 */
let ongoingAnimations = [];
function animate (element, keyframesFunction, durationInMs = 400, easing = 'ease-out', isResultsContainer = false) {
	return new Promise((resolve, reject) => {
		const existingAnimation = ongoingAnimations.find(object => object.element === element);
		if (existingAnimation) {
			existingAnimation.animation.cancel();
			EventDispatcher.dispatch('debug:add', 'onGoingAnimations', ongoingAnimations?.length || 0);
		}

		const keyframes = keyframesFunction(element);

		if (!keyframes.length) {
			return;
		}
		const options = { duration: durationInMs, easing: easing };
		if (isResultsContainer) {
			options.fill = 'forwards';
		}
		const animation = element.animate(keyframes, options);

		const currentAnimationObject = { element: element, animation: animation };

		ongoingAnimations.push(currentAnimationObject);

		const animationFinished = () => {
			ongoingAnimations = ongoingAnimations.filter(object => object.element !== element);
			resolve(element);
		};
		animation.onfinish = animationFinished;
		animation.oncancel = animationFinished;
	});
}
// function animate (element, keyframesFunction, durationInMs = 400, easing = 'ease-out') {
// 	return new Promise((resolve, reject) => {
// 		const animationFinished = () => {
// 			ongoingAnimations = ongoingAnimations.filter(object => object.element !== element);
// 			resolve(element);
// 		};

// 		const animationCancelled = () => {
// 			ongoingAnimations = ongoingAnimations.filter(object => object.element !== element);
// 			reject(element);
// 		};

// 		const existingAnimation = ongoingAnimations.find(object => object.element === element);
// 		if (existingAnimation) {
// 			existingAnimation.animation.cancel();
// 			let keyFrames = keyframesFunction(element);
// 			const computedStyle = getComputedStyle(element);
// 			const [startKeyframe] = keyFrames;
// 			const newStartKeyframe = {};

// 			Object.keys(startKeyframe).forEach((property) => {
// 				newStartKeyframe[property] = computedStyle.getPropertyValue(property);
// 			});

// 			// Replace the start keyframe with the new one
// 			keyFrames = [newStartKeyframe, ...keyFrames.slice(1)];
// 			// // update the duration remaining relative to the new duration
// 			// const timing = existingAnimation.animation.getComputedTiming();

// 			// // duration of the running animation
// 			// const activeDuration = timing.activeDuration;

// 			// // progress between 0 and 1 of the running animation
// 			// const activeProgress = timing.progress;

// 			// // calculate duration so that velocity is constant
// 			// durationInMs -= activeDuration - activeProgress * activeDuration;


// 			// Create a new animation with the new keyframes and duration
// 			const animation = element.animate(keyFrames, { duration: durationInMs, easing: easing });
// 			ongoingAnimations.push({ element: element, animation: animation });

// 			animation.onfinish = animationFinished;
// 			animation.oncancel = animationCancelled;
// 		} else {
// 			const keyFrames = keyframesFunction(element);
// 			if (!keyFrames.length) {
// 				return;
// 			}

// 			const animation = element.animate(keyFrames, { duration: durationInMs, easing: easing });
// 			ongoingAnimations.push({ element: element, animation: animation });

// 			animation.onfinish = animationFinished;
// 			animation.oncancel = animationCancelled;
// 		}
// 	});
// }

/**
 *
 * @param {Element} element Element to find sibling of
 * @param {string} selector Selector to filter by
 * @returns {Element|null} Next sibling or null if not found
 */
export function nextElement (element, selector) {
	// Get the next element
	const nextElem = element.nextElementSibling;

	// If there's no selector, return the next element
	if (!selector) {
		return nextElem;
	}

	// Otherwise, check if the element matches the selector
	if (nextElem && nextElem.matches(selector)) {
		return nextElem;
	}

	// if it's not a match, return null
	return null;
}

/**
 * @param {Element} elem
 * @returns boolean true if width or height > 0
 */
export function isVisible (elem) {
	return !!(elem.offsetWidth || elem.offsetHeight || elem.getClientRects().length);
}

/**
 * Add or remove items with a certain value from a primitive or complex array
 * @param {array} array array to toggle items in
 * @param {string|number|object|array} itemToToggle item(s) to toggle
 * @param {callback} [getValue] function that returns the value of item to toggle, only relevant for objects
 * @returns {array} a new array with the item(s) either added or removed
 * @example
 * const myArrayOfStrings = ['Tristan', 'Valle']
 * toggleArray(myArrayOfStrings, 'Tristan') // ['Valle']
 * const myArrayOfObjects = [{name: 'Valle', age: 49}, {name: 'Tristan', age: 31}]
 * toggleArray(myArrayOfObjects, {name: 'Tristan', age: 31}, item => `${item.name}|item.age`) // [{name: 'Valle', age: 49}]
 * const myArrayOfObjects = [{name: 'Valle', age: 49}, {name: 'Tristan', age: 31}]
 * toggleArray(myArrayOfObjects, [{name: 'Tristan', age: 31}, {name: 'Valle', age: 49}], item => `${item.name}|item.age`) // []
 */
export function arrayToggleValue (array, itemToToggle, getValue = item => item) {
	if (Array.isArray(itemToToggle)) {
		return itemToToggle.reduce((acc, item) => arrayToggleValue(acc, item, getValue), array);
	}

	const index = array.findIndex(item => getValue(item) === getValue(itemToToggle));
	if (index === -1) {
		return [...array, itemToToggle];
	}
	return removeAtIndex(array, index);
}

export function removeAtIndex (array, index, numberOfItemsToRemove = 1) {
	const copy = [...array];
	copy.splice(index, numberOfItemsToRemove);
	return copy;
}

/**
 * Encode a set of form elements as an array of names and values.
 * @param {Element} form
 * @returns {Array}
 */
export function serializeArray (form) {
	let field; let l; const s = [];
	if (typeof form === 'object' && form.nodeName === 'FORM') {
		const len = form.elements.length;
		for (let i = 0; i < len; i++) {
			field = form.elements[i];
			if (field.name && !field.disabled && field.type !== 'file' && field.type !== 'reset' && field.type !== 'submit' && field.type !== 'button') {
				if (field.type === 'select-multiple') {
					l = form.elements[i].options.length;
					for (let j = 0; j < l; j++) {
						if (field.options[j].selected) {
							s[s.length] = { name: field.name, value: field.options[j].value };
						}
					}
				} else if ((field.type !== 'checkbox' && field.type !== 'radio') || field.checked) {
					s[s.length] = { name: field.name, value: field.value };
				}
			}
		}
	}
	return s;
}

/**
 * Does what it says on the box ^_^
 * @param {any} variableToCheck
 * @returns {boolean}
 */
export function isEmptyObject (variableToCheck) {
	return Object.keys(variableToCheck).length === 0 && variableToCheck.constructor === Object;
}

/**
 * Does what it says on the tin =]
 * @param {callback} callback
 * @param {Number} wait
 * @returns
 */
export function debounce (callback, wait) {
	let timeout;
	return (...args) => {
		const context = this;
		clearTimeout(timeout);
		timeout = setTimeout(() => callback.apply(context, args), wait);
	};
}

/**
 *
 * @param {Element} element
 * @returns {Object} top and left offset of the element
 */
export function getOffset (element) {
	const elementReal = checkForElement(element);
	if (!elementReal) {
		console.warn('offset: Didn\'t find element', element);
		return;
	}
	const rect = elementReal.getBoundingClientRect();
	return {
		top: rect.top + globalThis.pageYOffset - document.documentElement.clientTop,
		left: rect.left + globalThis.pageXOffset - document.documentElement.clientLeft
	};
}

/**
 * Get the last element of an array or null
 * @param {array} array
 * @returns {any|null} last element of the array
 */
export function last (array) {
	return array?.[array.length - 1] || null;
}

export function addFilteredEventListener (targetSelector, eventType, closestSelector, callback) {
	const elementReal = checkForElement(targetSelector);
	if (!elementReal) {
		console.warn('addFilteredEventListener: Didn\'t find element', targetSelector);
		return;
	}
	elementReal.addEventListener(eventType, (event) => {
		const { target, stopPropagation, preventDefault } = event;
		const closest = event.target.closest(closestSelector);
		if (closest) {
			callback({ target, stopPropagation, preventDefault, closest });
		}
	});
}
/**
 *
 * @param {Object} filterObject object to clean
 * @param {array} keyWhitelist a series of keys to keep even if they are empty
 * @returns {Object}
 */
export function removeEmptyEntriesFromObject (filterObject, keyWhitelist = []) {
	// Get only the keys we are interested in
	const filterKeys = Object.keys(keyWhitelist.length ? keyWhitelist : filterObject);

	// Create a clean object that will contain the filter keys
	const cleanObject = {};

	// Loop over all entries in the store, filter the keys we are interested in
	Object.entries(filterObject).filter(([key]) => filterKeys.includes(key)).forEach(([key, value]) => {
		// If not an empty array/object then add it to `cleanObject`
		if (!isVariableEmpty(value)) {
			cleanObject[key] = value;
		}
	});
	return cleanObject;
}

export function isVariableEmpty (value) {
	const typeOfValue = typeof value;
	switch (typeOfValue) {
		case 'boolean':
		case 'string':
		case 'number':
			return false;
		case 'object':
			if (value === null) {
				return false;
			} else if (Array.isArray(value) && value.length > 0) {
				return false;
			} else if (Object.keys(value).length > 0) {
				return false;
			}
			return true;
		default:
			return true;
	}
}

/**
 * Attach the intersection observer to an element
 * Run a callback when that element scrolls into view
 * @param {Element} element dom node - the element to bind to
 * @param {callback} callback function to call when the element scrolls into view
 * @param {object} intersectionOptions options to pass to the intersection observer
 * @return none
 */
export function bindIntersectionObserver (element, callback, intersectionOptions) {
	const observer = new IntersectionObserver(callback, intersectionOptions);
	observer.observe(element);
}

export const documentReady = (callbackFunc) => {
	if (!document.addEventListener) {
		document.attachEvent('onreadystatechange', function () {
			if (document.readyState === 'complete') {
				callbackFunc();
			}
		});
	} else if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', callbackFunc);
	} else {
		callbackFunc();
	}
};

/**
 * @param {object} properties
 * @param {object} attribute
 * @param {string} text - optional source code
 * @return none
 */
export function loadJavascript (properties, attributes, onload = () => {}) {
	const script = document.createElement('script');

	script.onload = onload;

	// Set the properties
	for (const key in properties) {
		script[key] = properties[key];
	}

	// Set the attributes
	for (const key in attributes) {
		script.setAttribute(key, attributes[key]);
	}

	document.head.appendChild(script);
}

/**
 * @param {string} urlParameterName
 * @param {string} url
 * @param {any} defaultValue
 * @returns
 */
export function getUrlVarBase64 (urlParameterName, url = globalThis.location.href, defaultValue = {}) {
	let resp = defaultValue;
	try {
		const urlVars = new URLSearchParams(location.search).get(urlParameterName);
		resp = JSON.parse(Base64.decode(decodeURIComponent(urlVars)));
	} catch (error) {
		resp = defaultValue;
	}
	return resp;
}


/**
 *
 * @param {String} str
 * @returns {String}
 */
export function removeDotFromString (str) {
	return str.replace(/\./g, '');
}

/**
 * Generates a psuedo random string
 * @param {Number} length - Max length 15
 * @returns {String}
 */
export function genUID (length = -8) {
	return Math.random().toString(16).slice(length);
}

/**
 * Checks an objets messages matches a set of strings
 * @param {Error} error
 * @returns {Boolean}
 */
export function isNetworkError (error) {
	const networkErrorStrings = ['NetworkError', 'Load failed', 'Failed to fetch'];
	return networkErrorStrings.some(networkErrorString => error?.message?.includes?.(networkErrorString));
}
export function classList (element) {
	const elementReal = checkForElement(element)?.classList;
	if (!elementReal) {
		console.warn('classList: Didn\'t find element', element);
	}

	return {
		toggle: function (className, force) {
			if (force !== undefined) {
				elementReal?.toggle(className, !!force);
			} else {
				elementReal?.toggle(className);
			}
			return this;
		},
		add: function (...classNames) {
			elementReal?.add(...classNames);
			return this;
		},
		remove: function (...classNames) {
			elementReal?.remove(...classNames);
			return this;
		},
		contains: function (className) {
			return elementReal?.contains(className);
		}
	};
}


/**
 * @param {Object} element
 * @returns {Boolean}
 */
export function isError (obj) {
	return Object.prototype.toString.call(obj) === '[object Error]';
}


export function isAbortError (error) {
	console.log('isAbortError', error?.name);
	return error?.name === 'AbortError';
}

export function decimalToBinary (dec) {
	return (dec >>> 0).toString(2);
}

/**
 *
 * @param {Number} start
 * @returns {Number}
 */
export function timeExecution (start) {
	if (!start) {
		return Date.now();
	}
	return Math.round(Date.now() - start);
}

/**
 * Useful for sending data to the server, especially in unusual circumstances like beforeunload
 * @param {String} target
 * @param {String} method
 * @param {Array} parameters
 */
export function sendBeacon (target, method, parameters) {
	// if (typeof window?.navigator?.sendBeacon !== 'function') {
	// 	navigator.sendBeacon(
	// 		`${globalThis.location.protocol}//${globalThis.location.hostname}/conapi`,
	// 		JSON.stringify({
	// 			class: target,
	// 			method,
	// 			arguments: parameters
	// 		})
	// 	);
	// 	return;
	// }
	return ConnectApi(target, method, parameters);
}

/**
 * @description Converts an object to a query string
 * @param {Object} obj
 * @returns
 */
export function objectToQueryString (obj) {
	return Object.keys(obj).map(key => encodeURIComponent(key) + '=' + encodeURIComponent(obj[key])).join('&');
}

export function lerp (x, y, a) {
	return x * (1 - a) + y * a;
}

/**
 * Add event listeners to an array of elements
 * @param {Element[]} els 	array of elements
 * @param {String} ev		event
 * @param {Function} fn		function
 * @param {Object} options	object with options for the addEventListener
 * return none
 */
export function addEventListeners (els, ev, fn, options = false) {
	if (Array.isArray(els)) {
		els.forEach((el) => {
			if (isElement(el)) {
				el.addEventListener(ev, fn, options);
			} else {
				console.warn('addEventListeners: element incorrect');
			}
		});
	} else {
		console.warn('addEventListeners: array of elements is incorrect');
	}
}

export function encodeFilters (filters) {
	return Base64.encode(JSON.stringify(filters));
}

export function roundToNearest (num, nearest) {
	return Math.round(num / nearest) * nearest;
}

export function shareClick (shareData, disabledOnDesktop = true) {
	if (disabledOnDesktop && isDesktopView()) {
		return Promise.reject(new Error('Sharing disabled on desktop'));
	}
	if (navigator.canShare && navigator.canShare(shareData)) {
		return navigator.share(shareData);
	} else {
		return Promise.reject(new Error('Cannot share'));
	}
}


export function copyToClipboard (textToCopy) {
	if (navigator.clipboard) {
		return navigator.clipboard.writeText(textToCopy);
	}
	// Fallback for browsers without navigator.clipboard API
	return new Promise((resolve, reject) => {
		const textArea = document.createElement('textarea');
		textArea.value = textToCopy;
		textArea.style.position = 'fixed'; // Avoid scrolling to bottom
		document.body.appendChild(textArea);
		textArea.focus();
		textArea.select();
		try {
			const successful = document.execCommand('copy');
			document.body.removeChild(textArea);
			successful ? resolve() : reject(new Error('Failed to copy text to clipboard'));
		} catch (err) {
			document.body.removeChild(textArea);
			reject(err);
		}
	});
}

/**
 * Used for logging events
 * @param {string} event - This should be a verb
 * @param {string} eventMeta - This should be a noun
 * @returns {void}
 */
export function logEvent (event, eventCategory, meta = '') {
	if (event) {
		// In case we need to buffer the sendBeacons in the future
		// logInterval({ eventName: event, category: eventCategory, meta, pageClass: js_params.analyticsPageClass, pageName: js_params.analyticsPageName });
		console.debug({ eventName: event, category: eventCategory, meta, pageClass: js_params.analyticsPageClass, pageName: js_params.analyticsPageName });
		return sendBeacon('Vector', 'save', [[{ event, eventCategory, meta: meta, pageClass: js_params.analyticsPageClass, pageName: js_params.analyticsPageName }]]);
	}
}

/**
 * @param {Array} events
 * @returns {void}
 */
export function logEvents (events) {
	events = events.map(event => ({ ...event, pageClass: js_params.analyticsPageClass, pageName: js_params.analyticsPageName }));
	sendBeacon('Vector', 'save', [events]);
	console.debug(events);
}

/**
 * Begin an interval that logs events periodically
 * @param {Array} events
 * @returns {void}
 */
export function logInterval (event) {
	if (!window.eventCart) {
		window.eventCart = [];
	}
	window.eventCart.push(event);

	if (!window.eventInterval) {
		window.eventInterval = setInterval(() => {
			if (window.eventCart.length) {
				logEvents(window.eventCart);
				window.eventCart = [];
			}
		}, 3000);
	}
}

/**
 *
 * @param {HTMLElement} container holder of the tab trap behaviour
 * @param {string} containerName name of the container for descriptive purposes
 * @param {function} closeCallback callback to disable the tab trapping
 * @param {string} visibleClass class added to the element when it is tabbing, for any kind of special behaviour
 * @param {array of dom elements} tabbableElements list of tabbable inner elements, otherwise all the standard elements will be added
 * @param {boolean} tabApplyEnable if tab-apply class in inner elements will close the trapped element
 */
export function tabTrappingHelper (container, containerName, closeCallback, visibleClass = containerName + '--visible', tabbableElements, tabApplyEnable = true) {
	if (!window.js_params.isTabTrappingEnabled) {
		return;
	}

	if (!container || !containerName) {
		console.error(`Container not provided, can't execute container helper`);
		return;
	}

	const hiddenEl = container.querySelectorAll('[aria-hidden="true"]');

	hiddenEl.forEach((el) => {
		el.ariaHidden = 'false';
	});

	if (visibleClass && !container.classList.contains(visibleClass)) {
		container.classList.add(visibleClass);
	}

	if (!tabbableElements?.length) {
		tabbableElements = qsa('a, button, input:not([type="checkbox"]), .checkbox, textarea, select, .noUi-handle', container);
	}

	if (!tabbableElements?.length) {
		console.error(`No tabbable elements found in ${containerName}`);
		return;
	}

	// If there is no closing button
	let tabExitButton = container.querySelector('.tabulation-close');
	if (!tabExitButton) {
		// Create a close button
		tabExitButton = document.createElement('button');
		tabExitButton.classList.add('tabulation-close', 'icon', 'icon-circle-cross');
		tabExitButton.setAttribute('aria-label', `${lreplace('ariaLabels.closeContainer', { s: containerName })}`);
		container.append(tabExitButton);

		// Add event listener to close the dialog
		tabExitButton.addEventListener('click', exitTrapping);
	}

	tabbableElements.push(tabExitButton);

	// Enabling all tabs
	tabbableElements.forEach((link) => {
		link.tabIndex = 0;
	});

	tabbableElements[0].focus();

	function handleTrapping (event) {
		if (event.key === 'Tab') {
			if (event.shiftKey) {
				// Shift + Tab
				if (document.activeElement === tabbableElements[0]) {
					event.preventDefault();
					tabbableElements[tabbableElements.length - 1].focus();
				}
			} else {
				// Tab
				if (document.activeElement === tabbableElements[tabbableElements.length - 1]) {
					event.preventDefault();
					tabbableElements[0].focus();
				}
			}
		} else if (event.key === 'Escape') {
			// Escape to close the dialog
			exitTrapping();
		}
	}

	function exitTrapping () {
		container.parentElement.focus();

		// Disabling all tabs
		tabbableElements.forEach((link) => {
			link.tabIndex = -1;
		});

		classList(`.${visibleClass}`).remove(visibleClass);

		// Remove event listeners
		tabExitButton.removeEventListener('click', exitTrapping);
		tabExitButton.remove();
		container.removeEventListener('keydown', handleTrapping);

		// // Closing callback just in case
		if (typeof closeCallback === 'function') {
			closeCallback();
		}

		hiddenEl.forEach((el) => {
			el.ariaHidden = 'true';
		});
	}

	if (tabApplyEnable) {
		qsa('.tab-apply', container).forEach((elem) => {
			elem.addEventListener('click', exitTrapping);
		});
	}
	container.addEventListener('keydown', handleTrapping);

	return exitTrapping;
}

export function popStateCallback (onPopped) {
	let armed = false;

	return {
		arm () {
			if (armed) {
				return;
			}
			armed = true;

			history.pushState({ __modal: true }, '');
			window.addEventListener('popstate', onPopState, { once: true });
		},

		pop () {
			if (!armed) {
				return;
			}
			history.back();
		}
	};

	function onPopState () {
		armed = false;
		onPopped?.();
	}
}


/**
* Merge two arrays with only unique properties
* @param {array} array1 array to be merged
* @param {array} array2 array to be merged
* @returns {array}
*/
export function arrayMergePrimitive (array1, array2) {
	return [...new Set([...array1, ...array2])];
}


export function traceScroll () {
	// Helper function that wraps a method with a tracing wrapper.
	function traceWrap (obj, methodName) {
		const original = obj[methodName];
		if (typeof original !== 'function') {
			// Skip if it's not a function.
			return;
		}
		obj[methodName] = function (...args) {
			console.log(`Called ${methodName} with arguments:`, args);
			console.trace(); // This logs a call stack trace in the console.
			return original.apply(this, args);
		};
	}

	// Wrap global scrolling functions.
	traceWrap(window, 'scroll');
	traceWrap(window, 'scrollTo');
	traceWrap(window, 'scrollBy');

	// Wrap the element method for scrolling into view.
	if (Element.prototype.scrollIntoView) {
		traceWrap(Element.prototype, 'scrollIntoView');
	}

	// You can add additional wrappers for other scroll-related methods if necessary.
}


export function hashObject (obj) {
	return fnv1a32(JSON.stringify(stableStringify(obj)));
}

function fnv1a32 (str) {
	let h = 0x811C9DC5;
	for (let i = 0; i < str.length; i++) {
		h ^= str.charCodeAt(i);
		h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
	}
	return ('0000000' + h.toString(16)).slice(-8);
}

// 1) Turn an object into a *stable* string (keys sorted):
function stableStringify (obj) {
	if (obj === null) {
		return 'null';
	}
	if (typeof obj !== 'object') {
		return JSON.stringify(obj);
	}

	if (Array.isArray(obj)) {
		return '[' + obj.map(stableStringify).join(',') + ']';
	}

	// object
	const keys = Object.keys(obj).sort();
	return '{' + keys.map(k => JSON.stringify(k) + ':' + stableStringify(obj[k])).join(',') + '}';
}

/**
* Capitalize the first character of a string
* @param {String} word string to be capitalized
* @returns {String}
*/
export function capitalize (word) {
	if (!word || typeof word !== 'string') {
		return word;
	}

	return word.charAt(0).toUpperCase() + word.slice(1);
}

export function benchmark () {
	let timeStarted = null;
	function start () {
		timeStarted = performance.now();
	}

	function stop () {
		if (timeStarted) {
			const end = performance.now();
			const duration = end - timeStarted;
			timeStarted = null;
			console.log(`Duration:`, duration);
		}
		return null;
	}

	return {
		start,
		stop
	};
}

export function debugTranslationKeys (isVisible) {
	if (isVisible) {
		setCookie('inlineTranslation', isVisible);
	} else {
		eraseCookie('inlineTranslation');
	}
	location.reload();
}

export function humanJoin (arr) {
	if (!Array.isArray(arr) || arr.length === 0) {
		return '';
	}
	if (arr.length === 1) {
		return arr[0];
	}
	const lastItem = arr.pop();
	return `${arr.join(', ')} ${l('global.simplePhrases.and')} ${lastItem}`;
}

/**
 * Function to generate a promo feature floating div
 *
 * @param {*} el the element that should the promote feature arrow point at
 * @param {*} name for local storage hide
 * @param {*} message the message inside the bubble
 * @param {*} display the position of the promote alert, default is down
 */
export function promoteFeature (el, name, message, display = 'down') {
	// Don't show when shown or when we can't know if shown
	if (getCookie('consent') === null || storedItemRead('promote-' + name) !== null) {
		return;
	}

	if (!el) {
		console.error(`Element not provided, promote feature not executed.`);
		return;
	}

	const parent = el.parentNode;

	const parentRect = parent.getBoundingClientRect();

	const elRect = el.getBoundingClientRect();
	const centerFromLeft = (elRect.left - parentRect.left) + (elRect.width / 3);

	const span = document.createElement('span');
	span.classList.add('promote-feature');

	if (display !== 'down') {
		span.classList.add('promote-feature--' + display);
	}

	const border = document.createElement('div');
	border.classList.add('modal__borderAnimation');

	const innards = document.createElement('div');
	innards.classList.add('innards');

	const text = document.createElement('text');
	text.innerHTML = message;
	text.classList.add('promote-feature--text');

	innards.append(text);
	span.append(border, innards);

	span.style.left = centerFromLeft + 'px';

	parent.append(span);

	parent.classList.add('promote-shiny');
	parent.style.position = 'relative';

	const destroyPromote = () => {
		parent.classList.remove('promote-shiny');
		storedItemCreate('promote-' + name, true);
		span.remove();
	};

	span.addEventListener('click', destroyPromote);
	parent.addEventListener('click', destroyPromote);

	return destroyPromote;
}


// This function creates a map instance, the pertinent maptiles folder must be populated
export function createLeafletMap (leaflet, el, options = {}) {
	const map = leaflet.map(el, options);
	map.attributionControl.setPrefix(false);

	let directory = '';

	if (window?.js_params?.language) {
		directory = '/maptiles/' + window.js_params.language + '/{z}/{x}/{y}.png';
	} else {
		directory = '/maptiles/{z}/{x}/{y}.png';
	}

	console.log(directory);

	leaflet.tileLayer(directory, {
		minNativeZoom: 0,
		maxNativeZoom: 10,
		maxZoom: 10,
		attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
	}).addTo(map);
	return map;
}

export { lc, lm, l, lLink, lreplace, overrideTranslationFunctions };

export const CITY_ALIASES = window.js_params?.cityAliases ?? {};

export function cityDisplayName (cityName) {
	const alias = CITY_ALIASES[cityName];
	return alias ? `${cityName} (${alias})` : cityName;
}


export function requestAnimationFramePromise () {
	return new Promise(requestAnimationFrame);
}
